import { chunk } from "lodash";
import React from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { ActivityIndicator, ScrollView, StyleSheet, View } from "react-native";
import { PdFieldset, PdFormLabel } from "src/components/form-control";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { PdHookFormErrorMessage, PdHookFormInput, PdHookFormSelect } from "src/components/hook-form";
import { PdStack } from "src/components/layout";

import { PdModal, PdModalCloseButton, PdModalTitle } from "src/components/overlay";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { tUseModal, useModal } from "src/providers/OverlayProvider";
import { setupBrandAccount, setupRepAccount, setupRetailerRewards } from "src/redux/company/actions";
import { useAppDispatch } from "src/redux";
import { tModalComponentProps } from "src/redux/modals/types";
import { getBrandSetupAccount, getRepSetupAccount, getRetailerSetupRewards } from "src/util/apiNew";
import { countriesOptions, countryUSOption, tCountryOption } from "src/util/comboBoxData";
import { PdFormHelperText } from "src/components/v2/forms/control";
import { tVendorData } from "tmt_api/src/types/types";
import { useQuery } from "../api";
import { useIsMobile } from "../ui";

import { brandSetupForms } from "./vendor-setup-account-utils";

type tVendorSetupAccountModal = {
  mode: "brand" | "retailer" | "rep";
  defaultValue?: tVendorData;
  isEditing?: boolean;
};

type tFormData = {
  country: tCountryOption | null;
  emailAddress: string;
  businessName: string;
  ein: string;
  businessDomain: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  routingNumber: string;
  routingNumber2?: string;
  accountNumber: string;
};

const Form: React.FC<
  Pick<tModalComponentProps<tVendorSetupAccountModal>, "mode" | "defaultValue" | "onClose" | "onResolve">
> = ({ mode, defaultValue, onClose, onResolve }) => {
  const [isMutating, setMutating] = React.useState(false);
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const formMethods = useForm<tFormData>({
    defaultValues: {
      country:
        mode === "rep"
          ? countryUSOption
          : defaultValue?.country
          ? countriesOptions.find((country) => country.value === defaultValue.country) ?? null
          : null,
      emailAddress: defaultValue?.emailAddress || "",
      businessName: defaultValue?.businessName || "",
      ein: defaultValue?.ein || "",
      businessDomain: defaultValue?.businessDomain || "",
      address1: defaultValue?.address1 || "",
      address2: defaultValue?.address2 || "",
      city: defaultValue?.city || "",
      state: defaultValue?.state || "",
      zipCode: defaultValue?.zipCode || "",
      phone: defaultValue?.phone || "",
      routingNumber: (defaultValue?.routingNumber.split("-")?.[0] ?? defaultValue?.routingNumber) || "",
      routingNumber2: (defaultValue?.routingNumber.split("-")?.[1] ?? defaultValue?.routingNumber) || "",
      accountNumber: defaultValue?.accountNumber || "",
    },
  });
  const country: tCountryOption | null = useWatch({ name: "country", control: formMethods.control });
  const form = brandSetupForms[country?.value ?? countryUSOption.value];

  const handleOnClose = () => {
    onClose();
  };

  const handleOnSubmit = formMethods.handleSubmit(async (formData) => {
    try {
      setMutating(true);
      const data = {
        country: formData.country?.value || "",
        emailAddress: formData.emailAddress,
        businessName: formData.businessName,
        ein: formData.ein,
        businessDomain: formData.businessDomain,
        url: formData.businessDomain,
        address1: formData.address1,
        address2: formData.address2,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        phone: formData.phone,
        accountNumber: formData.accountNumber,
        routingNumber: formData.routingNumber2
          ? `${formData.routingNumber}${formData.routingNumber2}`
          : formData.routingNumber,
      };
      if (mode === "brand") await dispatch(setupBrandAccount(data));
      else if (mode === "retailer") await dispatch(setupRetailerRewards(data));
      else if (mode === "rep") await dispatch(setupRepAccount(data));
      onResolve();
    } catch (error) {
      setMutating(false);
    }
  });

  return (
    <FormProvider {...formMethods}>
      <View style={[styles.mb4, styles.wFull]}>
        <PdFieldset style={styles.mb4}>Business Information</PdFieldset>
        <View style={[styles.mb3, styles.zIndex1]}>
          <PdFormLabel>Country *</PdFormLabel>
          <PdHookFormSelect
            name="country"
            rules={{ required: "Required" }}
            placeholder="Write your country..."
            options={countriesOptions}
            isClearable={false}
            onSubmitEditing={handleOnSubmit}
            isDisabled={mode === "rep"}
          />
          <PdHookFormErrorMessage name="country" />
        </View>
        {chunk(form.businessFields, 2).map((businessFieldRow) => (
          <PdStack direction={isMobile ? "vertical" : "horizontal"} layout={[2, 2]} gap={3} style={styles.mb3}>
            {businessFieldRow.map(({ label, helperText, ...fieldProps }) => (
              <View>
                <PdFormLabel>{label}</PdFormLabel>
                <PdHookFormInput {...fieldProps} />
                {helperText && <PdFormHelperText>{helperText}</PdFormHelperText>}
                <PdHookFormErrorMessage name={fieldProps.name} />
              </View>
            ))}
          </PdStack>
        ))}
      </View>

      <View style={[styles.mb4, styles.wFull]}>
        <PdFieldset style={styles.mb4}>Contact details</PdFieldset>
        {chunk(form.contactFields, 2).map((contactFieldRow) => (
          <PdStack direction={isMobile ? "vertical" : "horizontal"} layout={[2, 2]} gap={3} style={styles.mb3}>
            {contactFieldRow.map(({ label, ...fieldProps }) => (
              <View>
                <PdFormLabel>{label}</PdFormLabel>
                <PdHookFormInput {...fieldProps} />
                <PdHookFormErrorMessage name={fieldProps.name} />
              </View>
            ))}
          </PdStack>
        ))}
      </View>

      <View style={[styles.mb4, styles.wFull]}>
        <PdFieldset style={styles.mb4}>Banking information</PdFieldset>
        {chunk(form.bankingFields, 2).map((bankingFieldRow) => (
          <PdStack direction={isMobile ? "vertical" : "horizontal"} layout={[2, 2]} gap={3} style={styles.mb3}>
            {bankingFieldRow.map(({ label, ...fieldProps }) => (
              <View>
                <PdFormLabel>{label}</PdFormLabel>
                <PdHookFormInput {...fieldProps} />
                <PdHookFormErrorMessage name={fieldProps.name} />
              </View>
            ))}
          </PdStack>
        ))}
      </View>

      <PdStack
        direction={isMobile ? "vertical" : "horizontal"}
        isReversed={isMobile}
        layout={[1, 1]}
        gap={3}
        style={styles.wFull}
      >
        <PdButton colorScheme="purple" variant="outline" onPress={handleOnClose}>
          <PdButtonLabel>Cancel</PdButtonLabel>
        </PdButton>
        <PdButton isLoading={isMutating} onPress={handleOnSubmit}>
          <PdButtonLabel>Save</PdButtonLabel>
        </PdButton>
      </PdStack>
    </FormProvider>
  );
};

const ModalContent: React.FC<
  Pick<tModalComponentProps<tVendorSetupAccountModal>, "mode" | "defaultValue" | "isEditing" | "onClose" | "onResolve">
> = ({ mode, defaultValue, isEditing, onClose, onResolve }) => {
  const { isLoading, data } = useQuery(
    React.useCallback(() => {
      if (mode === "brand") return getBrandSetupAccount();
      if (mode === "retailer") return getRetailerSetupRewards();
      return getRepSetupAccount();
    }, [mode]),
    { enabled: isEditing },
  );

  if (isLoading) {
    return <ActivityIndicator style={styles.modalLoading} color={kColors.brandDarkGreen} size="small" />;
  }

  return (
    <>
      <PdModalTitle style={styles.mb5}>Setup Account</PdModalTitle>
      <Form defaultValue={data || defaultValue} mode={mode} onClose={onClose} onResolve={onResolve} />
    </>
  );
};

export const VendorSetupAccount: React.FC<tModalComponentProps<tVendorSetupAccountModal>> = ({
  mode,
  defaultValue,
  isEditing,
  onClose,
  onResolve,
}) => {
  const isMobile = useIsMobile();

  const handleOnClose = () => {
    onClose();
  };

  return (
    <PdModal style={styles.modal} onClose={onClose} closeOnOverlayClick={!isMobile}>
      <PdModalCloseButton onPress={handleOnClose} />
      <ScrollView contentContainerStyle={styles.modalBody}>
        <ModalContent
          mode={mode}
          defaultValue={defaultValue}
          isEditing={isEditing}
          onClose={onClose}
          onResolve={onResolve}
        />
      </ScrollView>
    </PdModal>
  );
};

const useVendorSetupAccount = (): tUseModal<tVendorSetupAccountModal> => {
  return useModal<tVendorSetupAccountModal>(VendorSetupAccount);
};

export default useVendorSetupAccount;

const styles = StyleSheet.create({
  mb3: {
    marginBottom: 3 * kStyleConst.kSpacing,
  },
  mb4: {
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  mb5: {
    marginBottom: kStyleConst.kSpacing * 5,
  },
  modal: {
    marginHorizontal: 8 * kStyleConst.kSpacing,
    maxWidth: 600,
    width: "100%",
  },
  modalBody: {
    alignItems: "flex-start",
    backgroundColor: kColors.lightBrand,
    paddingHorizontal: 6 * kStyleConst.kSpacing,
    paddingVertical: 8 * kStyleConst.kSpacing,
  },
  modalLoading: {
    alignItems: "center",
    height: 250,
    justifyContent: "center",
    width: "100%",
  },
  wFull: {
    width: "100%",
  },
  zIndex1: {
    zIndex: 1,
  },
});
